import { useEffect, useReducer } from 'react';

import {
  Button,
  ChakraHStack,
  ChakraText,
  DateFormat,
  DatePicker,
  EquipUITheme,
  isDateRangeValid,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import Header from '~/components/common/Header';
import { PAGE_LAYOUT } from '~/lib/constants';
import {
  PatientActivityFilterReducerActions as FilterActions,
  PatientActivityFilterType,
  defaultActivityFilterState,
} from '~/lib/constants/patientActivity.constants';

export type PatientActivityFiltersAction = {
  type: FilterActions;
  value?: string | PatientActivityFilterType;
};

export type PatientActivityFiltersReducer = (
  state: PatientActivityFilterType,
  action: PatientActivityFiltersAction,
) => PatientActivityFilterType;

interface PatientActivityActionBarProps {
  onFilterChange: (val: PatientActivityFilterType) => void;
}
const filterReducer = (
  state: PatientActivityFilterType,
  action: PatientActivityFiltersAction,
): PatientActivityFilterType => {
  const { type, value } = action;
  switch (type) {
    case FilterActions.UPDATE_FROM_DATE:
      return {
        ...state,
        fromDate: value as string,
      };
    case FilterActions.UPDATE_TO_DATE:
      return {
        ...state,
        toDate: value as string,
      };
    case FilterActions.UPDATE_ALL_FILTER:
      return { ...(action.value as PatientActivityFilterType) };
    default:
      throw new Error();
  }
};
const TOP_LEVEL_ID = 'patient-activity-table_action-bar';
const { captionBold } = EquipUITheme.typography;

const PatientActivityActionBar = ({
  onFilterChange,
}: PatientActivityActionBarProps) => {
  const [filterState, dispatch] = useReducer<PatientActivityFiltersReducer>(
    filterReducer,
    defaultActivityFilterState,
  );
  const handleFromDateSelect = (date: Date): void => {
    dispatch({
      type: FilterActions.UPDATE_FROM_DATE,
      value: date
        ? DateTime.fromJSDate(date).toFormat(DateFormat.shortYearMonthDay)
        : null,
    });
  };

  const handleToDateSelect = (date: Date): void => {
    dispatch({
      type: FilterActions.UPDATE_TO_DATE,
      value: date
        ? DateTime.fromJSDate(date).toFormat(DateFormat.shortYearMonthDay)
        : null,
    });
  };

  // If "toDate" filter is now invalid, update it
  useEffect(() => {
    const { fromDate, toDate } = filterState ?? {};
    if (fromDate && toDate && !isDateRangeValid(fromDate, toDate)) {
      dispatch({
        type: FilterActions.UPDATE_TO_DATE,
        value: fromDate,
      });
    }
  }, [filterState?.fromDate]);

  // If "fromDate" filter is now invalid, update it
  useEffect(() => {
    const { fromDate, toDate } = filterState ?? {};

    if (fromDate && toDate && !isDateRangeValid(fromDate, toDate)) {
      dispatch({
        type: FilterActions.UPDATE_FROM_DATE,
        value: toDate,
      });
    }
  }, [filterState?.toDate]);

  return (
    <Header
      actions={
        <ChakraHStack paddingRight={PAGE_LAYOUT.rightSpacing}>
          <ChakraText
            {...captionBold}
            color="neutral.secondary"
            id={`${TOP_LEVEL_ID}__date-range__label`}
            minWidth="max-content"
          >
            Date Range
          </ChakraText>
          <DatePicker
            id={`${TOP_LEVEL_ID}__from-date`}
            onSelect={handleFromDateSelect}
            placeHolderText="MM/DD/YYYY"
            value={DateTime.fromFormat(
              filterState.fromDate ?? '',
              DateFormat.shortYearMonthDay,
            ).toJSDate()}
          />
          <DatePicker
            id={`${TOP_LEVEL_ID}__to-date`}
            onSelect={handleToDateSelect}
            placeHolderText="MM/DD/YYYY"
            value={DateTime.fromFormat(
              filterState.toDate ?? '',
              DateFormat.shortYearMonthDay,
            ).toJSDate()}
          />
          <Button
            boxShadow="none"
            id={`${TOP_LEVEL_ID}__apply`}
            onClick={() => {
              onFilterChange(filterState);
            }}
          >
            Apply
          </Button>
        </ChakraHStack>
      }
      title="Activity Log"
    />
  );
};

export default PatientActivityActionBar;
